import type { Track } from '@tla/types';

/** Geographic extent [lon/lat] used to fetch aligned satellite imagery. */
export interface Geo {
  minLon: number;
  maxLon: number;
  minLat: number;
  maxLat: number;
}

/** A projected racing line with per-sample distance, enough to place gates. */
export interface GateLine {
  /** Projected local coordinates [x, y] (metres) per sample. */
  pts: [number, number][];
  /** Distance from lap start (metres) per sample. */
  distance: Float64Array;
  /** Total lap distance in metres. */
  lapDistance: number;
}

/** Longest satellite image edge (px) requested from the imagery proxy. */
export const SAT_MAX_DIM = 2048;

/**
 * Same-origin satellite imagery URL for a lon/lat bbox. The API proxy serves
 * Azure Maps imagery when a key is configured (see README), otherwise the
 * keyless Esri World Imagery export.
 */
export function satelliteUrl(geo: Geo, maxDim = SAT_MAX_DIM): string {
  const aspect = (geo.maxLon - geo.minLon) / (geo.maxLat - geo.minLat || 1);
  const w = aspect >= 1 ? maxDim : Math.round(maxDim * aspect);
  const h = aspect >= 1 ? Math.round(maxDim / aspect) : maxDim;
  const bbox = `${geo.minLon},${geo.minLat},${geo.maxLon},${geo.maxLat}`;
  return `/api/satellite?bbox=${bbox}&w=${w}&h=${h}`;
}

/** Binary-search the nearest sample index for a target distance. */
export function nearestByDistance(distance: Float64Array, target: number): number {
  let lo = 0;
  let hi = distance.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (distance[mid] < target) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(distance[lo - 1] - target) < Math.abs(distance[lo] - target)) return lo - 1;
  return lo;
}

/** Number of sectors: from the track's sector gates if defined, else three. */
export function sectorCountForTrack(track: Track | null | undefined): number {
  const gates = track?.sectors?.length ?? 0;
  return gates > 0 ? gates + 1 : 3;
}

/**
 * Draw the sector-start gates across a reference racing line: a solid white
 * S/F tick at distance 0, then dashed yellow S2, S3… ticks at evenly spaced
 * sector boundaries. Shared by the track map and the corner editor so both
 * render identical markers.
 *
 * `fx`/`fy` map projected metres to canvas pixels, `lw` converts a screen-space
 * pixel width to the current transform's units, and `fitScale` converts world
 * metres to fit-pixels (so ticks grow with zoom but never shrink below a
 * readable minimum).
 */
export function drawSectorGates(
  ctx: CanvasRenderingContext2D,
  lap: GateLine,
  sectorCount: number,
  fx: (x: number) => number,
  fy: (y: number) => number,
  lw: (px: number) => number,
  fitScale: number,
): void {
  if (sectorCount <= 1 || lap.lapDistance <= 0) return;
  ctx.save();
  const TRACK_HALF_WIDTH_M = 9;
  const half = Math.max(lw(13), TRACK_HALF_WIDTH_M * fitScale);
  const prevAlign = ctx.textAlign;
  const prevBaseline = ctx.textBaseline;
  for (let s = 0; s < sectorCount; s++) {
    const dTarget = (lap.lapDistance * s) / sectorCount;
    const idx = nearestByDistance(lap.distance, dTarget);
    const a = lap.pts[idx];
    const b = lap.pts[idx + 1 < lap.pts.length ? idx + 1 : idx - 1] ?? a;
    const ax = fx(a[0]);
    const ay = fy(a[1]);
    let tx = fx(b[0]) - ax;
    let ty = fy(b[1]) - ay;
    const len = Math.hypot(tx, ty) || 1;
    tx /= len;
    ty /= len;
    const px = -ty; // unit perpendicular to the direction of travel
    const py = tx;
    const isStart = s === 0;
    ctx.setLineDash(isStart ? [] : [lw(5), lw(4)]);
    ctx.strokeStyle = isStart ? 'rgba(255,255,255,0.95)' : 'rgba(255,214,64,0.95)';
    ctx.lineWidth = lw(isStart ? 2.5 : 1.75);
    ctx.beginPath();
    ctx.moveTo(ax + px * half, ay + py * half);
    ctx.lineTo(ax - px * half, ay - py * half);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.fillStyle = isStart ? '#ffffff' : '#ffd640';
    ctx.font = `${lw(11)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(isStart ? 'S/F' : `S${s + 1}`, ax + px * (half + lw(9)), ay + py * (half + lw(9)));
  }
  ctx.textAlign = prevAlign;
  ctx.textBaseline = prevBaseline;
  ctx.restore();
}

/**
 * Draw a single sector/start-finish gate as its actual line segment. `a` and `b`
 * are the gate's endpoints already projected to local metres; `fx`/`fy` map them
 * to canvas pixels. Start/finish is a solid white line, sector gates are dashed
 * yellow, matching {@link drawSectorGates}. The label sits just past the `a` end.
 */
export function drawGateLine(
  ctx: CanvasRenderingContext2D,
  a: [number, number],
  b: [number, number],
  label: string,
  isStart: boolean,
  fx: (x: number) => number,
  fy: (y: number) => number,
  lw: (px: number) => number,
): void {
  const ax = fx(a[0]);
  const ay = fy(a[1]);
  const bx = fx(b[0]);
  const by = fy(b[1]);
  ctx.save();
  const prevAlign = ctx.textAlign;
  const prevBaseline = ctx.textBaseline;
  ctx.setLineDash(isStart ? [] : [lw(5), lw(4)]);
  ctx.strokeStyle = isStart ? 'rgba(255,255,255,0.95)' : 'rgba(255,214,64,0.95)';
  ctx.lineWidth = lw(isStart ? 2.5 : 1.75);
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.lineTo(bx, by);
  ctx.stroke();

  // Label just beyond the `a` endpoint, along the gate direction.
  let dx = ax - bx;
  let dy = ay - by;
  const len = Math.hypot(dx, dy) || 1;
  dx /= len;
  dy /= len;
  ctx.setLineDash([]);
  ctx.fillStyle = isStart ? '#ffffff' : '#ffd640';
  ctx.font = `${lw(11)}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, ax + dx * lw(9), ay + dy * lw(9));
  ctx.textAlign = prevAlign;
  ctx.textBaseline = prevBaseline;
  ctx.restore();
}

/**
 * Build a perpendicular gate segment across the racing line at `idx`, returning
 * its two endpoints in the same local-metre frame as `pts`. `halfWidthM` is the
 * gate half-length in metres (so it spans the full racing surface). Shared by the
 * track editor when placing/moving a sector gate.
 */
export function perpendicularGate(
  pts: [number, number][],
  idx: number,
  halfWidthM = 14,
): { a: [number, number]; b: [number, number] } {
  const i = Math.max(0, Math.min(pts.length - 1, idx));
  const p = pts[i];
  const q = pts[i + 1 < pts.length ? i + 1 : i - 1] ?? p;
  let tx = q[0] - p[0];
  let ty = q[1] - p[1];
  const len = Math.hypot(tx, ty) || 1;
  tx /= len;
  ty /= len;
  // Perpendicular unit vector.
  const px = -ty;
  const py = tx;
  return {
    a: [p[0] + px * halfWidthM, p[1] + py * halfWidthM],
    b: [p[0] - px * halfWidthM, p[1] - py * halfWidthM],
  };
}

/** Centroid of a set of local-metre points. */
export function centroidOf(pts: [number, number][]): [number, number] {
  let sx = 0;
  let sy = 0;
  for (const [x, y] of pts) {
    sx += x;
    sy += y;
  }
  const n = pts.length || 1;
  return [sx / n, sy / n];
}

/**
 * Default corner-label position (local metres): pushed radially outward from the
 * track centroid past the apex, so labels sit off the racing surface and never
 * overlap the spline. `offsetM` is how far past the apex to place the label.
 */
export function outwardLabelPos(
  apex: [number, number],
  centroid: [number, number],
  offsetM = 16,
): [number, number] {
  let dx = apex[0] - centroid[0];
  let dy = apex[1] - centroid[1];
  const len = Math.hypot(dx, dy) || 1;
  dx /= len;
  dy /= len;
  return [apex[0] + dx * offsetM, apex[1] + dy * offsetM];
}
